import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { API_URL } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Plus, Loader2, Pencil, Trash2 } from 'lucide-react';
import ModuleBg from '../components/ModuleBg';

function fmt(v) { return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v); }

const MONTH_NAMES = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
const START_YEAR = 2026;
function getYears() {
    const years = [];
    const endYear = new Date().getFullYear() + 10;
    for (let y = START_YEAR; y <= endYear; y++) years.push(y);
    return years;
}
function getMonthsForYear(year) {
    return MONTH_NAMES.map((name, i) => ({
        value: `${year}-${String(i + 1).padStart(2, '0')}`,
        label: name,
        short: name.slice(0, 3),
    }));
}
const MONTH_SHORT = ['Jan', 'Fév', 'Mars', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sept', 'Oct', 'Nov', 'Déc'];
function formatMonthLabel(v) {
    const [y, m] = v.split('-');
    return `${MONTH_SHORT[parseInt(m) - 1] || m} ${y}`;
}

export default function FraisBureauPage() {
    const { user } = useAuth();
    const isAdmin = ['super_admin', 'admin', 'admin_agence'].includes(user?.role);
    const [fraisList, setFraisList] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showDialog, setShowDialog] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ commercial_id: '', libelle: '', montant: 0, mois: [], actif: true });
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const years = getYears();

    const fetchData = useCallback(async () => {
        try {
            const [fraisRes, commRes] = await Promise.all([
                axios.get(`${API_URL}/api/frais-bureau`, { withCredentials: true }),
                isAdmin ? axios.get(`${API_URL}/api/commerciaux`, { withCredentials: true }) : Promise.resolve({ data: [] })
            ]);
            setFraisList(fraisRes.data); setUsers(commRes.data);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, [isAdmin]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const saveFrais = async () => {
        try {
            if (editing) { await axios.put(`${API_URL}/api/frais-bureau/${editing.id}`, { commercial_id: form.commercial_id, libelle: form.libelle, montant: form.montant, mois: form.mois, actif: form.actif }, { withCredentials: true }); toast.success('Mis à jour'); }
            else { await axios.post(`${API_URL}/api/frais-bureau`, form, { withCredentials: true }); toast.success('Créé'); }
            setShowDialog(false); setEditing(null); fetchData();
        } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    };
    const deleteFrais = async (id) => { if (!window.confirm('Supprimer ?')) return; try { await axios.delete(`${API_URL}/api/frais-bureau/${id}`, { withCredentials: true }); toast.success('Supprimé'); fetchData(); } catch (e) { toast.error('Erreur'); } };
    const openNew = () => { setEditing(null); setForm({ commercial_id: '', libelle: '', montant: 0, mois: [], actif: true }); setShowDialog(true); };
    const openEdit = (fb) => { setEditing(fb); setForm({ commercial_id: fb.commercial_id, libelle: fb.libelle, montant: fb.montant, mois: fb.mois, actif: fb.actif }); setShowDialog(true); };
    const toggleMonth = (v) => setForm(p => ({ ...p, mois: p.mois.includes(v) ? p.mois.filter(m => m !== v) : [...p.mois, v].sort() }));

    const activeTotal = fraisList.filter(f => f.actif).reduce((s, f) => s + f.montant * f.mois.length, 0);

    if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>;

    return (
        <ModuleBg>
        <div className="max-w-[1100px] mx-auto px-6 py-10" data-testid="frais-bureau-page">
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h1 className="text-[15px] font-semibold text-gray-900">Frais de bureau</h1>
                    <p className="text-[12px] text-gray-400 mt-1">Total actif : <span className="text-gray-700 font-mono">{fmt(activeTotal)}</span></p>
                </div>
                {isAdmin && <Button onClick={openNew} className="bg-[#6366F1] hover:bg-[#4F46E5] text-white text-[13px] h-9 rounded-lg px-4 shadow-sm" data-testid="add-frais-btn"><Plus className="w-4 h-4 mr-1.5" />Nouveau frais</Button>}
            </div>

            {fraisList.length === 0 && <p className="text-center text-gray-400 py-16 text-[13px]">Aucun frais de bureau</p>}

            {fraisList.length > 0 && (
                <div className="card overflow-hidden">
                    <table className="w-full">
                        <thead><tr className="border-b border-gray-100">
                            {['Libellé','Commercial','Montant/mois','Mois','Statut',''].map(h => (
                                <th key={h} className={`px-4 py-2.5 text-[11px] text-gray-400 uppercase tracking-[0.1em] font-medium ${h === 'Montant/mois' ? 'text-right' : 'text-left'} ${h === 'Statut' ? 'text-center' : ''}`}>{h}</th>
                            ))}
                        </tr></thead>
                        <tbody>
                            {fraisList.map(fb => (
                                <tr key={fb.id} className={`border-b border-gray-50 hover:bg-gray-50/60 transition-colors ${!fb.actif ? 'opacity-50' : ''}`} data-testid={`frais-row-${fb.id}`}>
                                    <td className="px-4 py-3 text-[13px] text-gray-700">{fb.libelle}</td>
                                    <td className="px-4 py-3 text-[13px] text-gray-500">{fb.commercial_name || '—'}</td>
                                    <td className="px-4 py-3 text-right text-[13px] font-mono text-gray-700">{fmt(fb.montant)}</td>
                                    <td className="px-4 py-3 text-[12px] text-gray-400" title={fb.mois.map(formatMonthLabel).join(', ')}>{fb.mois.length} mois</td>
                                    <td className="px-4 py-3 text-center"><Badge className={`text-[10px] ${fb.actif ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>{fb.actif ? 'Actif' : 'Inactif'}</Badge></td>
                                    <td className="px-4 py-3">{isAdmin && <div className="flex gap-0.5 justify-end"><button onClick={() => openEdit(fb)} className="p-1.5 hover:bg-gray-100 rounded-md"><Pencil className="w-3.5 h-3.5 text-gray-400" /></button><button onClick={() => deleteFrais(fb.id)} className="p-1.5 hover:bg-red-50 rounded-md"><Trash2 className="w-3.5 h-3.5 text-red-400" /></button></div>}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <Dialog open={showDialog} onOpenChange={setShowDialog}>
                <DialogContent className="bg-white text-gray-900 max-w-lg">
                    <DialogHeader><DialogTitle>{editing ? 'Modifier' : 'Nouveau frais de bureau'}</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-1.5"><Label className="text-gray-500 text-[11px] uppercase tracking-wider">Commercial</Label><Select value={form.commercial_id || 'none'} onValueChange={v => setForm({...form, commercial_id: v === 'none' ? '' : v})}><SelectTrigger data-testid="frais-form-commercial"><SelectValue placeholder="Sélectionner..." /></SelectTrigger><SelectContent><SelectItem value="none" disabled>Sélectionner...</SelectItem>{users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent></Select></div>
                        <div className="space-y-1.5"><Label className="text-gray-500 text-[11px] uppercase tracking-wider">Libellé</Label><Input value={form.libelle} onChange={e => setForm({...form, libelle: e.target.value})} data-testid="frais-form-libelle" /></div>
                        <div className="space-y-1.5"><Label className="text-gray-500 text-[11px] uppercase tracking-wider">Montant mensuel (€)</Label><Input type="number" value={form.montant || ''} onChange={e => setForm({...form, montant: parseFloat(e.target.value) || 0})} data-testid="frais-form-montant" /></div>
                        <div className="space-y-1.5"><Label className="text-gray-500 text-[11px] uppercase tracking-wider">Mois applicables</Label>
                            {/* Year selector */}
                            <div className="flex items-center gap-2 mb-2">
                                <Select value={String(selectedYear)} onValueChange={v => setSelectedYear(parseInt(v))}>
                                    <SelectTrigger className="w-32 h-8 text-[12px]" data-testid="frais-year-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>{years.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
                                </Select>
                                <button type="button" onClick={() => { const ym = getMonthsForYear(selectedYear).map(m => m.value); setForm(p => ({ ...p, mois: [...new Set([...p.mois, ...ym])].sort() })); }} className="text-[11px] text-[#6366F1] hover:text-[#4F46E5] font-medium" data-testid="select-all-year">Toute l'année</button>
                                <button type="button" onClick={() => { const ym = getMonthsForYear(selectedYear).map(m => m.value); setForm(p => ({ ...p, mois: p.mois.filter(m => !ym.includes(m)) })); }} className="text-[11px] text-gray-400 hover:text-gray-600 font-medium">Désélectionner</button>
                            </div>
                            {/* Months grid */}
                            <div className="grid grid-cols-4 gap-1.5" data-testid="months-grid">{getMonthsForYear(selectedYear).map(m => (
                                <button key={m.value} type="button" onClick={() => toggleMonth(m.value)} className={`px-2 py-1.5 rounded-lg text-[11px] font-medium transition-colors ${form.mois.includes(m.value) ? 'bg-[#6366F1] text-white' : 'bg-gray-50 text-gray-500 hover:bg-gray-100'}`} data-testid={`month-btn-${m.value}`}>{m.label}</button>
                            ))}</div>
                            {/* Selected months summary */}
                            {form.mois.length > 0 && (
                                <p className="text-[11px] text-gray-400 mt-2" data-testid="months-summary">{form.mois.length} mois sélectionné{form.mois.length > 1 ? 's' : ''} : {form.mois.map(formatMonthLabel).join(', ')}</p>
                            )}
                        </div>
                        {editing && <div className="flex items-center justify-between"><Label className="text-gray-500 text-[11px]">Actif</Label><Switch checked={form.actif} onCheckedChange={v => setForm({...form, actif: v})} /></div>}
                    </div>
                    <DialogFooter><Button variant="outline" onClick={() => setShowDialog(false)}>Annuler</Button><Button onClick={saveFrais} disabled={!form.commercial_id} className="bg-[#6366F1] hover:bg-[#4F46E5] text-white disabled:opacity-40" data-testid="frais-form-submit">{editing ? 'Mettre à jour' : 'Créer'}</Button></DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
        </ModuleBg>
    );
}
